"use client";

import React, { ReactNode, useId, useMemo } from "react";
import SnapSlider from "@/components/SnapSlider/SnapSlider";

// Minimal slider config. Mirrors the subset of the old Swiper props this component actually reads
// (slidesPerView / spaceBetween / breakpoints / scrollbar / navigation / freeMode). The index
// signature lets existing consumers keep passing Swiper-only props (modules, mousewheel, ...) —
// they are simply ignored now that Swiper is gone, so no consumer needs to change.
type SliderBreakpoint = {
  slidesPerView?: number | "auto";
  spaceBetween?: number;
};

export type BaseSliderProps = {
  slidesPerView?: number | "auto";
  spaceBetween?: number;
  breakpoints?: Record<number, SliderBreakpoint>;
  scrollbar?: unknown;
  navigation?: unknown;
  freeMode?: unknown;
  // Accept and ignore leftover Swiper props (modules, mousewheel, ...).
  [key: string]: unknown;
};

interface IBaseSliderProps {
  children: ReactNode;
  sliderProps?: BaseSliderProps;
  className?: string;
  // Kept for API compatibility; height equalization is now handled by flexbox `items-stretch`.
  additionalHeight?: number;
  recalculationDependencyList?: (number | string)[];
}

// `slidesPerView: N` means N slides visible across the track, accounting for the (N-1) gaps between
// them. width = (100% - (N-1)*gap) / N. `auto` -> natural slide width (no width set).
const widthForView = (slidesPerView: number | "auto" | undefined, spaceBetween: number): string => {
  if (slidesPerView === undefined || slidesPerView === "auto") return "auto";
  const n = slidesPerView;
  return `calc((100% - ${(n - 1) * spaceBetween}px) / ${n})`;
};

// Was a Swiper wrapper (Scrollbar + Navigation modules, slidesPerView:"auto"). Rewritten on top of
// SnapSlider — a native CSS scroll-snap slider with vanilla controls — so Swiper (~33KB) stays out
// of the bundle. Public props are unchanged. `slidesPerView`/`spaceBetween`/`breakpoints` map to
// per-slide widths + gaps emitted as a tiny scoped <style> (needed because breakpoint values are
// dynamic numbers Tailwind can't pre-build). Like the old Swiper defaults, every BaseSlider shows
// the draggable 2px progress scrollbar; arrows render only when a consumer passes `navigation`.
const BaseSlider = React.memo<IBaseSliderProps>((props) => {
  const { children, sliderProps, className = "" } = props;

  // Unique, CSS-safe class so each instance scopes its own breakpoint styles.
  const rawId = useId();
  const scopeClass = `bs-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

  const itemRules = useMemo(() => {
    const defaults = {
      slidesPerView: "auto" as number | "auto",
      spaceBetween: 10,
      breakpoints: {
        320: { spaceBetween: 10 },
        550: { spaceBetween: 24 },
      } as Record<number, SliderBreakpoint>,
    };

    const merged = { ...defaults, ...(sliderProps ?? {}) };
    const baseSpace = merged.spaceBetween ?? 0;
    const baseView = merged.slidesPerView;
    const breakpoints = merged.breakpoints ?? {};

    // Base (mobile-first) rules.
    let rules = `.${scopeClass} { gap: ${baseSpace}px; } .${scopeClass} > * { flex: 0 0 ${widthForView(baseView, baseSpace)}; width: ${widthForView(baseView, baseSpace)}; }`;

    // Responsive overrides, smallest breakpoint first. Each breakpoint can override slidesPerView
    // and/or spaceBetween; spaceBetween carries forward from the previous active breakpoint.
    let runningSpace = baseSpace;
    let runningView = baseView;
    Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((bp) => {
        const cfg = breakpoints[bp];
        if (cfg.spaceBetween !== undefined) runningSpace = cfg.spaceBetween;
        if (cfg.slidesPerView !== undefined) runningView = cfg.slidesPerView;
        const w = widthForView(runningView, runningSpace);
        rules += `@media (min-width: ${bp}px) { .${scopeClass} { gap: ${runningSpace}px; } .${scopeClass} > * { flex: 0 0 ${w}; width: ${w}; } }`;
      });

    return rules;
  }, [sliderProps, scopeClass]);

  return (
    <div className={`relative ${className}`}>
      <style dangerouslySetInnerHTML={{ __html: itemRules }} />
      <SnapSlider
        // Old Swiper defaults: Scrollbar module with draggable:true was always on; Navigation was
        // registered but only rendered when the consumer enabled it.
        scrollbar={sliderProps?.scrollbar !== false}
        navigation={!!sliderProps?.navigation}
        snap={sliderProps?.freeMode ? "none" : "mandatory"}
        trackClassName={scopeClass}
      >
        {children}
      </SnapSlider>
    </div>
  );
});

BaseSlider.displayName = "BaseSlider";

export default BaseSlider;
